import { IErrorProps } from "@faire/web--source/slate/Input/error";
import * as React from "react";

import { ICustomOption } from "./__internal__/types";

export interface IOption<T extends string = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

export namespace IOption {
  export const build = <T extends string>(
    partial?: Partial<IOption<T>>
  ): IOption<T> => ({
    label: "",
    value: "" as T,
    ...(partial ?? {}),
  });
}

export interface ICreateNewOption {
  label:
    | React.ReactNode
    | ((searchValue: string | undefined) => React.ReactNode);
  onClick?: (searchValue: string | undefined) => void;
  closeDropdown: boolean;
  hideUnlessNoOptions?: boolean;
}

export interface IProps<T extends string = string> extends IErrorProps {
  className?: string;
  /** The currently selected dropdown value. */
  value?: T;
  /** Text for the label above the dropdown menu. */
  label?: string;
  /** Text to display when no value is selected. */
  placeholder?: HTMLInputElement["placeholder"];
  /** Text displayed beneath the dropdown. Will not display if an error message is provided. */
  helper?: string;
  /** Options provided in the dropdown. Each option contains a `label` and a `value` string. */
  options: IOption<T>[];
  /** handles when an option is selected from the dropdown. */
  onSelect: (option: IOption<T>) => void;
  /** handles when user clears the selected option **/
  onClear?: () => void;
  /** handles when the dropdown is opened. Note: on mweb, does not get called if the dropdown is opened via the arrow keys due to a limitation. */
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  /** Disables the dropdown and applies the `disabled` styles */
  disabled?: boolean;
  "data-test-id"?: string;
  /** What, if any, permission the user agent has to provide automated assistance in filling out dropdown values, as well as guidance to the browser as to the type of information expected in the field. */
  autoComplete?: string;
  /** id passed to the html input */
  id?: string;
  /** This prop disable type in search option. Only allow users to select options in dropdown menu.*/
  disableInput?: boolean;
  /** The element for the container that the dropdown should try to stay within
     will likely come from react modal. */
  containingElement?: HTMLElement;
  /** Hides the border of the input when it is not being focussed */
  borderless?: boolean;
  /** Filters results when searched */
  filter?: boolean;
  /** Allows for specifying a custom option that will appear at the end of the options list*/
  customOption?: ICustomOption<T>;
  "aria-label"?: string;
  /** Allows create new option as an option in dropdown menu*/
  createNewOption?: ICreateNewOption;
  /** Allows getting a ref to the input component instance. */
  inputRef?: React.RefObject<HTMLInputElement>;
  /** Allows for data to be visible in hotjar recordings */
  "data-hj-allow"?: boolean;
  /** Uses the tablet and above dropdown for mobile viewports */
  useTabletAndAboveDropdownStyle?: boolean;
}
